import React, { useState, useEffect } from 'react';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import NavInf from '../../components/NavInf/NavInf';
import DebtHistoryModal from '../../components/DebtHistoryModal/DebtHistoryModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { secureFetch } from '../../utils/secureFetch';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';
import Loader from '../../components/Loader/Loader';

import {
    faTriangleExclamation,
    faCalendar,
    faHourglass,
    faUserCheck,
} from '@fortawesome/free-solid-svg-icons';
import './ProviderDebtReport.css';

export default function ProviderDebtReport() {
    const [selectedTab, setSelectedTab] = useState('Todos');
    const [showModal, setShowModal] = useState(false);
    const [providers, setProviders] = useState([]);
    const [isZero, setIsZero] = useState(false);
    const [loading, setLoading] = useState(true);
    const [modalData, setModalData] = useState({
        provider: null,
        history: [],
    });

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                secureFetch('https://api.minymol.com/debts/providers')
                    .then(res => {
                        if (!res.ok) throw new Error('Error cargando proveedores');
                        return res.json();
                    })
                    .then(data => {
                        setProviders(data);
                        setLoading(false);
                        const totalDebt = data.reduce(
                            (sum, prov) => sum + prov.deudaRestante,
                            0
                        );
                        setIsZero(totalDebt === 0);
                    })
                    .catch(err => {
                        console.error(err);
                        setProviders([]);
                        setIsZero(true); // si falla, podemos asumir 0 para evitar bloqueos
                    });
            }
        });
        return unsubscribe;
    }, []);

    // Filtrar según la pestaña seleccionada
    // Después: filtras y ordenas de mayor a menor deuda
    const filteredProviders = providers
        .filter(p => {
            if (selectedTab === 'Todos') return true;
            
            if (selectedTab === 'Mora') {
                // Un proveedor está en mora si:
                // - Su estado es explícitamente 'mora'
                return p.estado === 'mora';
            }
            
            if (selectedTab === 'Al día') {
                // Un proveedor está al día si:
                // - Su estado es 'aldia' (sin deuda)
                // - Su estado es 'deuda' (tiene deuda pero está al día con pagos)
                // - Cualquier estado que NO sea 'mora'
                return p.estado === 'aldia' || p.estado === 'deuda' || p.estado !== 'mora';
            }
            
            return true;
        })
        .sort((a, b) => b.deudaRestante - a.deudaRestante);


    // Calcular resumen para la parte superior
    const deudaTotal = providers.reduce((sum, p) => sum + p.deudaRestante, 0);
    const countMora = providers.filter(p => p.estado === 'mora').length;
    const nextDue = providers
        .map(p => p.deudaMasAntigua)
        .filter(d => d)
        .sort(
            (a, b) =>
                new Date(a).getTime() - new Date(b).getTime()
        )[0] || '';

    const openModal = async (prov) => {
        try {
            const res = await secureFetch(`https://api.minymol.com/movements/by-provider/${prov.id}`);
            if (!res.ok) throw new Error('Error cargando historial');
            const json = await res.json();
            setModalData(json);
            setShowModal(true);
        } catch (e) {
            console.error(e);
            alert(e.message);
        }
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            <Header />
            <div className="debt-container">
                <div className={`summary-card ${isZero ? 'debt-zero' : ''}`}>
                    <div className="summary-header">
                        <h2>Deuda Total</h2>
                        <span className="total-amount">
                            ${deudaTotal.toLocaleString()}
                        </span>
                    </div>
                    <div className="summary-details">
                        <div className="summary-detail-item">
                            <div id="icon-summary-mora">
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                            </div>
                            <span>Proveedores en mora: {countMora}</span>
                        </div>
                        <div className="summary-detail-item">
                            <div id="icon-summary-calendar">
                                <FontAwesomeIcon icon={faCalendar} />
                            </div>
                            <span>Próximo pago: {nextDue}</span>
                        </div>
                    </div>
                </div>

                <div className="tabs-container">
                    <div
                        className="tabs-background"
                        style={{
                            transform: `translateX(${['Mora', 'Al día', 'Todos'].indexOf(selectedTab) * 100
                                }%)`,
                        }}
                    />
                    {['Mora', 'Al día', 'Todos'].map(tab => (
                        <button
                            key={tab}
                            className={`tab-button ${selectedTab === tab ? 'active' : ''
                                }`}
                            onClick={() => setSelectedTab(tab)}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                <div className="debt-list">
                    {filteredProviders.length === 0 ? (
                        <div className="no-debt-message">
                            <h3>No hay proveedores con deudas</h3>
                        </div>
                    ) : (
                        filteredProviders.map(prov => (
                            <div
                                key={prov.id}
                                className="debt-card"
                                onClick={() => openModal(prov)}
                            >
                                <div className="debt-header">
                                    <h3>{prov.name}</h3>
                                    <span>
                                        ${prov.deudaRestante.toLocaleString()}
                                    </span>
                                </div>

                                <div className="debt-info">
                                    {prov.estado === 'mora' ? (
                                        <div className="debt-info-item">
                                            <div>
                                                <FontAwesomeIcon icon={faHourglass} />
                                            </div>
                                            <span>Vence en: {prov.deudaMasAntigua}</span>
                                        </div>
                                    ) : (
                                        <div className="debt-info-item">
                                            <div>
                                                <FontAwesomeIcon
                                                    style={{ color: '#3949ab' }}
                                                    icon={faCalendar}
                                                />
                                            </div>
                                            <span>{prov.deudaMasAntigua ? `Vence el:  ${prov.deudaMasAntigua}` : 'No tiene deuda'}</span>
                                        </div>
                                    )}

                                    <div className="debt-info-item">
                                        <div
                                            className={`icon ${prov.estado === 'mora' ? 'red' : 'green'
                                                }`}
                                        >
                                            {prov.estado === 'mora' ? (
                                                <FontAwesomeIcon
                                                    style={{ color: 'red' }}
                                                    icon={faTriangleExclamation}
                                                />
                                            ) : (
                                                <FontAwesomeIcon
                                                    style={{ color: 'green' }}
                                                    icon={faUserCheck}
                                                />
                                            )}
                                        </div>
                                        <span>
                                            Estado:{' '}
                                            {prov.estado === 'mora' ? 'Mora' : 'Al día'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )))}
                </div>
            </div>

            <DebtHistoryModal
                visible={showModal}
                onClose={() => setShowModal(false)}
                provider={modalData.provider || { name: '', totalPaid: 0, status: 'aldia' }}
                history={modalData.history || []}
            />

            <NavInf />
            <Footer navinf={true} />
        </>
    );
}
